/**
 * Token + auth-mode store (W-X-02).
 *
 * Bearer-only auth (no cookies): login returns the JWT; we hold the access + refresh tokens and
 * send `Authorization: Bearer …` on every request. Tokens are persisted to localStorage so the
 * session survives a page reload (there is no cookie session to fall back on). In a WebView the
 * shell is additionally notified via postMessage on login so it can register the FCM device.
 */

export type AuthMode = "cookie" | "bearer";

// Exported so AuthContext can notice another tab signing in or out (D-02).
export const ACCESS_KEY = "dp.access_token";
const REFRESH_KEY = "dp.refresh_token";

let accessToken: string | null = readStorage(ACCESS_KEY);
let refreshToken: string | null = readStorage(REFRESH_KEY);

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

// Keep the in-memory copy in sync with another tab writing/clearing tokens.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === ACCESS_KEY) accessToken = e.newValue;
    if (e.key === REFRESH_KEY) refreshToken = e.newValue;
  });
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode) — fall back to in-memory only */
  }
}

export function getAuthMode(): AuthMode {
  // Always bearer — the backend issues a JWT and sets no session cookie.
  return "bearer";
}

export function getAccessToken(): string | null {
  // Fall back to storage in case this module loaded before the token was written.
  if (accessToken === null) accessToken = readStorage(ACCESS_KEY);
  return accessToken;
}

export function getRefreshToken(): string | null {
  if (refreshToken === null) refreshToken = readStorage(REFRESH_KEY);
  return refreshToken;
}

export function setTokens(access: string | null, refresh: string | null): void {
  accessToken = access;
  refreshToken = refresh;
  writeStorage(ACCESS_KEY, access);
  writeStorage(REFRESH_KEY, refresh);
}

/**
 * Re-read the tokens from storage — another tab may have just renewed them — without waiting for
 * the `storage` event to arrive.
 */
export function syncTokensFromStorage(): void {
  accessToken = readStorage(ACCESS_KEY);
  refreshToken = readStorage(REFRESH_KEY);
}

export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  writeStorage(ACCESS_KEY, null);
  writeStorage(REFRESH_KEY, null);
}
